export enum IdentificationType {
    DUNS = 'DUNS',
    CRN = 'CRN',
    RNGSY = 'RNGSY',
    RNIOM = 'RNIOM',
    RNJSY = 'RNJSY',
    COC = 'COC',
    TVADE = 'TVADE',
    CREF = 'CREF',
    ACN = 'ACN',
    ABN = 'ABN',
    COFIS = 'COFIS',
    TVAIT = 'TVAIT',
    CCIAA = 'CCIAA',
    SIREN = 'SIREN',
    TVAFR = 'TVAFR',
    NIF = 'NIF',
    TVABE = 'TVABE',
    RCBE = 'RCBE',
    KVK = 'KVK',
    UID = 'UID',
    COCCH = 'COCCH',
    EIN = 'EIN',
    BN = 'BN',
    SSN = 'SSN',
    RNSI = 'RNSI',
    TVASI = 'TVASI',
    OIB = 'OIB',
    RNSE = 'RNSE',
    TVARO = 'TVARO',
    RNRO = 'RNRO',
    VATGR = 'VATGR',
    GEMI = 'GEMI',
    ARMAE = 'ARMAE',
    BULSTAT = 'BULSTAT',
    TVABG = 'TVABG',
    RNBG = 'RNBG',
    VATLT = 'VATLT',
    RCLT = 'RCLT',
    TVADK = 'TVADK',
    FN = 'FN',
    RNCY = 'RNCY',
    VATCY = 'VATCY',
    RCEE = 'RCEE',
    VATEE = 'VATEE',
    TVAFI = 'TVAFI',
    RNHU = 'RNHU',
    VATHU = 'VATHU',
    RNIS = 'RNIS',
    RCLV = 'RCLV',
    VATLV = 'VATLV',
    COCLI = 'COCLI',
    RCLU = 'RCLU',
    IBLC = 'IBLC',
    CNMT = 'CNMT',
    RCI = 'RCI',
    NZBN = 'NZBN',
    RNNZ = 'RNNZ',
    RNNO = 'RNNO',
    TVAPL = 'TVAPL',
    RNPL = 'RNPL',
    REGON = 'REGON',
    RNPT = 'RNPT',
    COESM = 'COESM',
    ICOSK = 'ICOSK',
    NRNHK = 'NRNHK',
    VATHK = 'VATHK',
}

/**
 * VAT/Tax identification types
 * Used to distinguish VAT numbers from registration numbers
 */
export const VAT_IDENTIFICATION_TYPES: ReadonlySet<IdentificationType> = new Set([
    IdentificationType.TVADE,
    IdentificationType.TVAIT,
    IdentificationType.TVAFR,
    IdentificationType.TVABE,
    IdentificationType.TVASI,
    IdentificationType.TVARO,
    IdentificationType.TVABG,
    IdentificationType.TVADK,
    IdentificationType.TVAFI,
    IdentificationType.TVAPL,
    IdentificationType.VATGR,
    IdentificationType.VATLT,
    IdentificationType.VATCY,
    IdentificationType.VATEE,
    IdentificationType.VATHU,
    IdentificationType.VATLV,
    IdentificationType.VATHK,
    IdentificationType.IBLC, // Luxembourg VAT
]);

/**
 * Third-party identifier types (not government-issued registration numbers)
 * These are identifiers assigned by private organizations, not official government registrations.
 * - DUNS: Dun & Bradstreet Universal Numbering System
 */
export const THIRD_PARTY_IDENTIFIER_TYPES: ReadonlySet<IdentificationType> = new Set([IdentificationType.DUNS]);

export interface ExtractedIdentifications {
    registrationNumber?: string;
    dunsNumber?: string;
    vatNumber?: string;
}

/**
 * Extracts registration number, DUNS, and VAT number from identifications array
 * - DUNS: type === 'DUNS'
 * - VAT: types in VAT_IDENTIFICATION_TYPES
 * - Registration: everything else (CRN, COC, SIREN, KVK, etc.)
 */
export function extractIdentifications(identifications?: { type: string; code: string }[]): ExtractedIdentifications {
    if (!identifications?.length) {
        return {};
    }

    const dunsId = identifications.find((id) => id.type === IdentificationType.DUNS);
    const vatId = identifications.find((id) => VAT_IDENTIFICATION_TYPES.has(id.type as IdentificationType));
    const registrationId = identifications.find(
        (id) => id.type !== IdentificationType.DUNS && !VAT_IDENTIFICATION_TYPES.has(id.type as IdentificationType),
    );

    return {
        dunsNumber: dunsId?.code,
        vatNumber: vatId?.code,
        registrationNumber: registrationId?.code,
    };
}

const formatRegexes: Record<string, RegExp> = {
    N: /^\d+$/,
    A: /^[a-zA-Z]+$/,
    AN: /^[a-zA-Z\d]+$/,
};

/**
 * Validates an identification code against its type's format and length constraints.
 * Returns true if valid, false otherwise.
 */
export function isValidIdentification(type: IdentificationType, code: string): boolean {
    const typeDef = identificationTypesData.find(({ code: c }) => c === type);
    if (!typeDef) return false;

    // Check custom regex if defined (takes precedence over format/length checks)
    if (typeDef.regex) {
        return new RegExp(typeDef.regex).test(code);
    }

    // Check format constraints
    if (typeDef.format && formatRegexes[typeDef.format] && !formatRegexes[typeDef.format].test(code)) {
        return false;
    }

    // Check length constraints
    if (typeDef.minLength && code.length < typeDef.minLength) return false;
    if (typeDef.maxLength && code.length > typeDef.maxLength) return false;

    return true;
}

export const identificationTypesData = [
    {
        country: 'US',
        code: 'DUNS',
        label: 'DUNS number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'DUN',
        atradiusCode: 'DB',
    },
    {
        country: 'HK',
        code: 'NRNHK',
        label: 'National registration number',
        atradiusCode: 'NRN',
        minLength: 1,
        maxLength: 15,
    },
    {
        country: 'HK',
        code: 'VATHK',
        label: 'VAT Number',
        atradiusCode: 'VAT',
        minLength: 1,
        maxLength: 15,
    },
    {
        country: 'CA',
        code: 'DUNS',
        label: 'DUNS number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'DUN',
    },
    {
        country: 'GB',
        code: 'CRN',
        label: 'Company registration number (CRN)',
        minLength: 8,
        maxLength: 8,
        format: 'AN',
        priority: 1,
        allianzCode: 'CRO',
        regex: '^(\\d{8}|[a-zA-Z]{1}\\d{7}|[a-zA-Z]{2}\\d{6})$',
    },
    {
        country: 'GB',
        code: 'RNGSY',
        label: 'Reg number Guernsey',
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        priority: 2,
        allianzCode: 'RNGSY',
    },
    {
        country: 'GB',
        code: 'RNIOM',
        label: 'Reg number Isle of Man',
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        priority: 2,
        allianzCode: 'RNIOM',
    },
    {
        country: 'GB',
        code: 'RNJSY',
        label: 'Reg number Jersey',
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        priority: 2,
        allianzCode: 'RNJSY',
    },
    {
        country: 'DE',
        code: 'COC',
        label: 'HR (Handelsregister)',
        minLength: 1,
        maxLength: 20,
        format: 'AN',
        priority: 1,
        allianzCode: 'COC',
    },
    {
        country: 'DE',
        code: 'TVADE',
        label: 'VAT number',
        minLength: 9,
        maxLength: 15,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVADE',
    },
    {
        country: 'DE',
        code: 'CREF',
        label: 'Creditreform (agency)',
        minLength: 10,
        maxLength: 10,
        format: 'N',
        priority: 3,
        allianzCode: 'CREF',
    },
    {
        country: 'AU',
        code: 'ACN',
        label: 'ACN (Australia)',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'ACN',
    },
    {
        country: 'AU',
        code: 'ABN',
        label: 'Australian business number (ABN)',
        minLength: 11,
        maxLength: 11,
        format: 'N',
        priority: 2,
        allianzCode: 'ABN',
    },
    {
        country: 'AU',
        code: 'DUNS',
        label: 'DUNS number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 3,
        allianzCode: 'DUN',
    },
    {
        country: 'IT',
        code: 'COFIS',
        label: 'COFIS Reg number Italy',
        minLength: 11,
        maxLength: 16,
        format: 'AN',
        priority: 1,
        allianzCode: 'COFIS',
    },
    {
        country: 'IT',
        code: 'TVAIT',
        label: 'Partita IVA',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVAIT',
        regex: '^(IT)?\\d{11}$',
    },
    {
        country: 'IT',
        code: 'CCIAA',
        label: 'CCIAA (Italy)',
        minLength: 3,
        maxLength: 12,
        format: 'AN',
        priority: 3,
        allianzCode: 'CCIAA',
    },
    {
        country: 'FR',
        code: 'SIREN',
        label: 'SIREN number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'SIREN',
    },
    {
        country: 'FR',
        code: 'TVAFR',
        label: 'VAT number France',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVAFR',
        regex: '^(FR)?\\d{11}$',
    },
    {
        country: 'ES',
        code: 'NIF',
        label: 'Número de Identificación Fiscal',
        minLength: 9,
        maxLength: 9,
        format: 'AN',
        priority: 1,
        allianzCode: 'CIF',
        regex: '^(ES)?([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[ABCDEFGHJKLMNPQRSUVW][0-9]{7}[0-9A-Z])$',
    },
    {
        country: 'BE',
        code: 'TVABE',
        label: 'VAT number Belgium',
        minLength: 10,
        maxLength: 12,
        format: 'AN',
        priority: 1,
        allianzCode: 'TVABE',
    },
    {
        country: 'BE',
        code: 'RCBE',
        label: 'Trade Register - Belgium',
        minLength: 8,
        maxLength: 9,
        format: 'AN',
        priority: 2,
        allianzCode: 'RCBE',
    },
    {
        country: 'NL',
        code: 'KVK',
        label: 'KVK number',
        minLength: 12,
        maxLength: 14,
        format: 'AN',
        priority: 1,
        allianzCode: 'KVK',
        regex: '^(\\d{8}|(NL)?\\d{9}B\\d{2})$',
    },
    {
        country: 'GP',
        code: 'SIREN',
        label: 'SIREN number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'SIREN',
    },
    {
        country: 'GP',
        code: 'TVAFR',
        label: 'VAT number France',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVAFR',
        regex: '^(FR)?\\d{11}$',
    },
    {
        country: 'RE',
        code: 'SIREN',
        label: 'SIREN number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'SIREN',
    },
    {
        country: 'RE',
        code: 'TVAFR',
        label: 'VAT number France',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVAFR',
        regex: '^(FR)?\\d{11}$',
    },
    {
        country: 'MQ',
        code: 'SIREN',
        label: 'SIREN number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'SIREN',
    },
    {
        country: 'MQ',
        code: 'TVAFR',
        label: 'VAT number France',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVAFR',
        regex: '^(FR)?\\d{11}$',
    },
    {
        country: 'CH',
        code: 'UID',
        label: 'Chamber of commerce UID',
        minLength: 9,
        maxLength: 15,
        priority: 1,
        allianzCode: 'COCCHE',
    },
    {
        country: 'CH',
        code: 'COCCH',
        label: 'Chamber of commerce CH',
        minLength: 11,
        maxLength: 18,
        format: 'AN',
        priority: 2,
        allianzCode: 'COCCH',
    },
    {
        country: 'US',
        code: 'EIN',
        label: 'EIN',
        minLength: 9,
        maxLength: 9,
        format: 'N',
    },
    {
        code: 'SSN',
        label: 'SSN',
        minLength: 9,
        maxLength: 9,
        format: 'N',
    },
    {
        country: 'CA',
        code: 'BN',
        label: 'Business number',
        minLength: 9,
        maxLength: 15,
        format: 'AN',
    },
    {
        country: 'SI',
        code: 'RNSI',
        label: 'Reg number Slovenia',
        minLength: 1,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'RNSI',
    },
    {
        country: 'SI',
        code: 'TVASI',
        label: 'VAT for Slovenia',
        minLength: 8,
        maxLength: 10,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVASI',
    },
    {
        country: 'HR',
        code: 'OIB',
        label: 'OIB ID Number',
        minLength: 11,
        maxLength: 11,
        format: 'N',
        priority: 1,
        allianzCode: 'OIB',
    },
    {
        country: 'SE',
        code: 'RNSE',
        label: 'Reg number Sweden',
        minLength: 10,
        maxLength: 10,
        format: 'N',
        priority: 1,
        allianzCode: 'RNSE',
    },
    {
        country: 'RO',
        code: 'TVARO',
        label: 'VAT number Romania',
        minLength: 1,
        maxLength: 10,
        format: 'AN',
        priority: 1,
        allianzCode: 'TVARO',
    },
    {
        country: 'RO',
        code: 'RNRO',
        label: 'Reg number Romania',
        minLength: 1,
        maxLength: 13,
        priority: 2,
        allianzCode: 'RNRO',
    },
    {
        country: 'GR',
        code: 'VATGR',
        label: 'VAT for Greece',
        minLength: 9,
        maxLength: 11,
        format: 'AN',
        priority: 1,
        allianzCode: 'VATGR',
    },
    {
        country: 'GR',
        code: 'GEMI',
        label: 'GEMI Number',
        minLength: 12,
        maxLength: 12,
        format: 'N',
        priority: 2,
        allianzCode: 'GEMI',
    },
    {
        country: 'GR',
        code: 'ARMAE',
        label: 'ARMAE for Greece',
        minLength: 1,
        maxLength: 6,
        format: 'N',
        priority: 3,
        allianzCode: 'ARMAE',
    },
    {
        country: 'BG',
        code: 'BULSTAT',
        label: 'Bulgarian register no.',
        minLength: 9,
        maxLength: 10,
        format: 'N',
        priority: 1,
        allianzCode: 'BULSTAT',
    },
    {
        country: 'BG',
        code: 'TVABG',
        label: 'VAT for Bulgaria',
        minLength: 9,
        maxLength: 12,
        format: 'AN',
        priority: 2,
        allianzCode: 'TVABG',
    },
    {
        country: 'BG',
        code: 'RNBG',
        label: 'Reg number Bulgaria',
        minLength: 5,
        maxLength: 10,
        format: 'AN',
        priority: 3,
        allianzCode: 'RNBG',
        regex: '^\\d{3,5}/\\d{4}$',
    },
    {
        countryCode: 'LT',
        code: 'RCLT',
        label: 'Trade Regist. - Lithuania',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 1,
        allianzCode: 'RCLT',
    },
    {
        country: 'LT',
        code: 'VATLT',
        label: 'VAT Lithuania',
        minLength: 9,
        maxLength: 14,
        format: 'AN',
        priority: 2,
        allianzCode: 'VATLT',
    },
    {
        country: 'DK',
        code: 'TVADK',
        label: 'CVR (Denmark)',
        minLength: 8,
        maxLength: 13,
        format: 'AN',
        priority: 1,
        allianzCode: 'TVADK',
    },
    {
        country: 'DK',
        code: 'DUNS',
        label: 'DUNS number',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        priority: 2,
        allianzCode: 'DUN',
    },
    {
        country: 'AT',
        code: 'FN',
        label: 'FN (Austria)',
        priority: 1,
        minLength: 2,
        maxLength: 9,
        format: 'AN',
        allianzCode: 'FN',
    },
    {
        country: 'CY',
        code: 'RNCY',
        label: 'Reg number Cyprus',
        priority: 1,
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        allianzCode: 'RNCY',
    },
    {
        country: 'CY',
        code: 'VATCY',
        label: 'Tax Reg Nr Cyprus',
        priority: 2,
        minLength: 9,
        maxLength: 10,
        format: 'AN',
        allianzCode: 'VATCY',
    },
    {
        code: 'RCEE',
        label: 'Trade Register - Estonia',
        priority: 1,
        country: 'EE',
        minLength: 8,
        maxLength: 8,
        format: 'N',
        allianzCode: 'RCEE',
    },
    {
        code: 'VATEE',
        label: 'VAT Estonia',
        priority: 2,
        country: 'EE',
        minLength: 9,
        maxLength: 11,
        format: 'AN',
        allianzCode: 'VATEE',
    },
    {
        code: 'TVAFI',
        label: 'VAT number Finland',
        priority: 1,
        country: 'FI',
        minLength: 8,
        maxLength: 10,
        format: 'AN',
        allianzCode: 'TVAFI',
    },
    {
        code: 'RNHU',
        label: 'Reg number Hungary',
        priority: 1,
        country: 'HU',
        minLength: 10,
        maxLength: 12,
        format: 'AN',
        allianzCode: 'RNHU',
        regex: '^\\d{2}-\\d{2}-\\d{6}$',
    },
    {
        code: 'VATHU',
        label: 'VAT number for HU',
        priority: 2,
        country: 'HU',
        minLength: 8,
        maxLength: 10,
        format: 'AN',
        allianzCode: 'VATHU',
    },
    {
        code: 'RNIS',
        label: 'Reg number Iceland',
        priority: 1,
        country: 'IS',
        minLength: 10,
        maxLength: 10,
        format: 'N',
        allianzCode: 'RNIS',
    },
    {
        code: 'RCLV',
        label: 'Trade Register - Latvia',
        priority: 1,
        country: 'LV',
        minLength: 11,
        maxLength: 11,
        format: 'N',
        allianzCode: 'RCLV',
    },
    {
        code: 'VATLV',
        label: 'VAT Latvia',
        priority: 2,
        country: 'LV',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        allianzCode: 'VATLV',
    },
    {
        code: 'COCLI',
        label: 'Chamber of commerce LI',
        priority: 1,
        country: 'LI',
        minLength: 12,
        maxLength: 17,
        format: 'AN',
        allianzCode: 'COCLI',
    },
    {
        code: 'RCLU',
        label: 'Trade Register - Luxem.',
        priority: 1,
        country: 'LU',
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        allianzCode: 'RCLU',
    },
    {
        code: 'IBLC',
        label: 'IBLC Luxembourg (VAT)',
        priority: 2,
        country: 'LU',
        minLength: 8,
        maxLength: 10,
        format: 'AN',
        allianzCode: 'IBLC',
    },
    {
        code: 'CNMT',
        label: 'Malta company number',
        priority: 1,
        country: 'MT',
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        allianzCode: 'CNMT',
    },
    {
        code: 'RCI',
        label: 'RCI Monaco',
        priority: 1,
        country: 'MC',
        minLength: 1,
        maxLength: 8,
        format: 'AN',
        allianzCode: 'RCI',
    },
    {
        code: 'TVAFR',
        label: 'VAT number France',
        priority: 2,
        country: 'MC',
        minLength: 11,
        maxLength: 13,
        format: 'AN',
        allianzCode: 'TVAFR',
    },
    {
        code: 'SIREN',
        label: 'SIREN number',
        priority: 3,
        country: 'MC',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        allianzCode: 'SIREN',
    },
    {
        code: 'NZBN',
        label: 'NZ Business Number',
        priority: 1,
        country: 'NZ',
        minLength: 13,
        maxLength: 13,
        format: 'N',
        allianzCode: 'NZBN',
    },
    {
        code: 'RNNZ',
        label: 'Reg number New Zealand',
        priority: 2,
        country: 'NZ',
        minLength: 2,
        maxLength: 8,
        format: 'N',
        allianzCode: 'RNNZ',
    },
    {
        code: 'DUNS',
        label: 'DUNS number',
        priority: 3,
        country: 'NZ',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        allianzCode: 'DUN',
    },
    {
        code: 'RNNO',
        label: 'Reg number Norway',
        priority: 1,
        country: 'NO',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        allianzCode: 'RNNO',
    },
    {
        code: 'TVAPL',
        label: 'NIP - VAT number - Poland',
        priority: 1,
        country: 'PL',
        minLength: 10,
        maxLength: 12,
        format: 'AN',
        allianzCode: 'TVAPL',
    },
    {
        code: 'RNPL',
        label: 'Reg number Poland',
        priority: 2,
        country: 'PL',
        minLength: 10,
        maxLength: 10,
        format: 'N',
        allianzCode: 'RNPL',
    },
    {
        code: 'REGON',
        label: 'Statistical nb (REGON)',
        priority: 3,
        country: 'PL',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        allianzCode: 'REGON',
    },
    {
        code: 'RNPT',
        label: 'NIPC (Reg Nr Portugal)',
        priority: 1,
        country: 'PT',
        minLength: 9,
        maxLength: 9,
        format: 'N',
        allianzCode: 'RNPT',
    },
    {
        code: 'COESM',
        label: 'Codice Operatore Economic',
        priority: 1,
        country: 'SM',
        minLength: 3,
        maxLength: 7,
        format: 'AN',
        allianzCode: 'COESM',
    },
    {
        code: 'ICOSK',
        label: 'ICOSK (Slovakia)',
        priority: 1,
        country: 'SK',
        minLength: 8,
        maxLength: 8,
        format: 'N',
        allianzCode: 'ICOSK',
    },
];
